import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Input,
  Textarea,
  Dropdown,
  Option,
} from '@fluentui/react-components'
import { useCaseStore, CaseType } from '../stores/caseStore'
import { useAuthStore } from '../stores/authStore'

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
})

interface CreateCaseDialogProps {
  open: boolean
  onClose: () => void
}

export function CreateCaseDialog({ open, onClose }: CreateCaseDialogProps) {
  const styles = useStyles()
  const addCase = useCaseStore((s) => s.addCase)
  const userId = useAuthStore((s) => s.userId)
  
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [caseType, setCaseType] = useState<CaseType>('investigation')
  const [error, setError] = useState('')

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Case name is required')
      return
    }
    
    if (!userId) {
      setError('User not authenticated')
      return
    }

    addCase(name.trim(), description.trim(), caseType, userId)
    
    // Reset form
    setName('')
    setDescription('')
    setCaseType('investigation')
    setError('')
    
    onClose()
  }

  const handleClose = () => {
    // Reset form on close
    setName('')
    setDescription('')
    setCaseType('investigation')
    setError('')
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Create New Case</DialogTitle>
          <DialogContent className={styles.content}>
            <Field
              label="Case Name"
              required
              validationMessage={error}
              validationState={error ? 'error' : 'none'}
            >
              <Input
                value={name}
                onChange={(_, data) => {
                  setName(data.value)
                  setError('')
                }}
                placeholder="Enter case name"
              />
            </Field>

            <Field label="Description">
              <Textarea
                value={description}
                onChange={(_, data) => setDescription(data.value)}
                placeholder="Enter case description (optional)"
                rows={4}
              />
            </Field>

            <Field label="Case Type">
              <Dropdown
                value={caseType}
                selectedOptions={[caseType]}
                onOptionSelect={(_, data) => setCaseType(data.optionValue as CaseType)}
              >
                <Option value="investigation">Investigation</Option>
                <Option value="surveillance">Surveillance</Option>
                <Option value="incident">Incident</Option>
                <Option value="other">Other</Option>
              </Dropdown>
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSubmit}>
              Create Case
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
